import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star, MessageSquare, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { format } from 'date-fns';

interface Rating {
  id: string;
  customer_id: string;
  venue_id: string;
  booking_id?: string;
  rating: number;
  review_text?: string;
  rating_type: 'booking_experience' | 'event_experience';
  created_at: string;
  customer_name?: string;
}

interface VenueRatingSystemProps {
  venueId: string;
  bookingId?: string;
  ratingType: 'booking_experience' | 'event_experience';
  onRatingSubmitted?: () => void;
}

export const VenueRatingSystem: React.FC<VenueRatingSystemProps> = ({
  venueId,
  bookingId,
  ratingType,
  onRatingSubmitted
}) => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  // Use unified user logic - prioritize internal user
  const currentUser = internalUser || supabaseUser;
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  useEffect(() => {
    checkExistingRating();
  }, [venueId, bookingId, currentUser]);

  const checkExistingRating = async () => {
    if (!currentUser || !venueId) return;

    try {
      let query = supabase
        .from('venue_customer_ratings')
        .select('id, rating, review_text, rating_type')
        .eq('venue_id', venueId)
        .eq('customer_id', currentUser.id)
        .eq('rating_type', ratingType);

      if (bookingId) {
        query = query.eq('booking_id', bookingId);
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking existing rating:', error);
        return;
      }

      if (data) {
        setHasRated(true);
        setRating(data.rating);
        setReviewText(data.review_text || '');
      }
    } catch (error) {
      console.error('Error checking existing rating:', error);
    }
  };

  const handleStarClick = (starValue: number) => {
    if (!hasRated) {
      setRating(starValue);
    }
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      toast.error('Please log in to submit a rating');
      return;
    }

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      const ratingData = {
        venue_id: venueId,
        customer_id: currentUser.id,
        booking_id: bookingId,
        rating,
        review_text: reviewText.trim() || null,
        rating_type: ratingType,
      };

      const { error } = await supabase
        .from('venue_customer_ratings')
        .insert(ratingData);

      if (error) throw error;

      setHasRated(true);
      toast.success('Rating submitted successfully!');
      onRatingSubmitted?.();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser) {
  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent className="p-4 text-center">
        <p className="text-gray-400">Please log in to rate this venue</p>
      </CardContent>
    </Card>
  );
  }

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Star className="h-5 w-5" />
          Rate Your {ratingType === 'booking_experience' ? 'Booking' : 'Event'} Experience
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasRated ? (
          <div className="space-y-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-6 w-6 ${
                    star <= rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
              <span className="ml-2 text-sm text-muted-foreground">
                Your rating: {rating}/5
              </span>
            </div>
            {reviewText && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm">{reviewText}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Thank you for your feedback!
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Rating *</Label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-8 w-8 cursor-pointer transition-colors ${
                      star <= rating
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-200'
                    }`}
                    onClick={() => handleStarClick(star)}
                  />
                ))}
                {rating > 0 && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    {rating}/5
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="review">Review (Optional)</Label>
              <Textarea
                id="review"
                placeholder={`Tell us about your ${ratingType === 'booking_experience' ? 'booking' : 'event'} experience...`}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={3}
              />
            </div>

            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting || rating === 0}
              className="w-full"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

interface VenueRatingDisplayProps {
  venueId: string;
  showReviews?: boolean;
}

export const VenueRatingDisplay: React.FC<VenueRatingDisplayProps> = ({
  venueId,
  showReviews = true
}) => {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalRatings: 0,
    bookingExperienceAvg: 0,
    eventExperienceAvg: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRatings();
  }, [venueId]);

  const loadRatings = async () => {
    try {
      const { data: ratingsData, error } = await supabase
        .from('venue_customer_ratings')
        .select(`
          id,
          rating,
          review_text,
          rating_type,
          created_at,
          customer_id,
          profiles!venue_customer_ratings_customer_id_fkey(first_name, last_name)
        `)
        .eq('venue_id', venueId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch internal user profiles separately for internal users
      const internalUserIds = (ratingsData || [])
        .filter(rating => !rating.profiles)
        .map(rating => rating.customer_id);
      
      let internalProfiles = {};
      if (internalUserIds.length > 0) {
        const { data: internalData } = await supabase
          .from('internal_users')
          .select('id, username')
          .in('id', internalUserIds);
        
        if (internalData) {
          internalProfiles = internalData.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
          }, {});
        }
      }

      const ratingsWithNames = (ratingsData || []).map(rating => {
        let customerName = 'Anonymous User';
        
        if (rating.profiles) {
          // Supabase user
          customerName = `${rating.profiles.first_name || ''} ${rating.profiles.last_name || ''}`.trim();
        } else if (internalProfiles[rating.customer_id]) {
          // Internal user
          const internalUser = internalProfiles[rating.customer_id];
          customerName = internalUser.username || 'Anonymous User';
        }

        return {
          id: rating.id,
          customer_id: rating.customer_id,
          venue_id: venueId,
          rating: rating.rating || 0,
          review_text: rating.review_text,
          rating_type: rating.rating_type as 'booking_experience' | 'event_experience',
          created_at: rating.created_at,
          customer_name: customerName
        };
      });

      setRatings(ratingsWithNames);

      // Calculate statistics
      const totalRatings = ratingsWithNames.length;
      const averageRating = totalRatings > 0 
        ? ratingsWithNames.reduce((sum, r) => sum + (r.rating || 0), 0) / totalRatings 
        : 0;

      const bookingRatings = ratingsWithNames.filter(r => r.rating_type === 'booking_experience');
      const eventRatings = ratingsWithNames.filter(r => r.rating_type === 'event_experience');

      const bookingExperienceAvg = bookingRatings.length > 0
        ? bookingRatings.reduce((sum, r) => sum + (r.rating || 0), 0) / bookingRatings.length
        : 0;

      const eventExperienceAvg = eventRatings.length > 0
        ? eventRatings.reduce((sum, r) => sum + (r.rating || 0), 0) / eventRatings.length
        : 0;

      setStats({
        averageRating,
        totalRatings,
        bookingExperienceAvg,
        eventExperienceAvg,
      });
    } catch (error) {
      console.error('Error loading ratings:', error);
      toast.error('Failed to load venue ratings');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-8 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Rating Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            Venue Ratings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                <span className="text-2xl font-bold">
                  {stats.averageRating.toFixed(1)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Overall Rating ({stats.totalRatings} reviews)
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <ThumbsUp className="h-5 w-5 text-blue-500" />
                <span className="text-xl font-semibold">
                  {stats.bookingExperienceAvg.toFixed(1)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Booking Experience
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <MessageSquare className="h-5 w-5 text-green-500" />
                <span className="text-xl font-semibold">
                  {stats.eventExperienceAvg.toFixed(1)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Event Experience
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Individual Reviews */}
      {showReviews && ratings.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Recent Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {ratings.slice(0, 5).map((rating) => (
                <div key={rating.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{rating.customer_name}</span>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${
                                star <= rating.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {rating.rating_type === 'booking_experience' ? 'Booking Experience' : 'Event Experience'} • 
                        {format(new Date(rating.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  {rating.review_text && (
                    <p className="text-sm">{rating.review_text}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};